import {
    Organization,
    CONNECTIONS_HOME_PATH,
    curatedOrganizationKey,
    isConnectionsOnlyOrg,
    isOtsarOrganization,
} from '@/lib/organizations';

export function getDefaultRoute(payloadOrUser: { role_code?: string; organization_name?: string } | null | undefined): string {
    // Fresh self-signup users (no organization yet) explore on the pricing page
    // first. Once they join Blueberries Plus, usePlusLock moves them to the
    // /welcome Plus home (and /welcome bounces not-yet-joined users back here).
    if (payloadOrUser?.role_code === 'PLATFORM_USER') {
        return '/pricing';
    }

    // Platform admins keep the admin home / org selector on /workspace.
    if (payloadOrUser?.role_code === 'ADMIN') {
        return '/workspace';
    }

    if (payloadOrUser?.organization_name === Organization.RESTAURA) {
        return '/restaura';
    }

    if (payloadOrUser?.organization_name === Organization.KAVKOM) {
        return '/kpis-dashboard';
    }

    if (payloadOrUser?.organization_name === Organization.ADVANCED_RETAIL) {
        return '/financial';
    }

    // Nova Capital is the demo org (shown as 770 Capital). Land on Act 1 of the
    // walkthrough so impersonation opens on Connect, not a later act.
    if (payloadOrUser?.organization_name === Organization.NOVA_CAPITAL) {
        return '/demo/integrations';
    }

    // Wayflyer's only page is the /wayflyer demo, a static sidebar entry (not
    // a /workspace/[pageKey] layout page) — same situation as Nova Capital
    // above, so land directly on it rather than stalling on /workspace.
    if (payloadOrUser?.organization_name === Organization.WAYFLYER) {
        return '/wayflyer';
    }

    // Cross River Bank runs on the shipped screens rather than on layout-API
    // pages, so /workspace would stall on "Redirecting…". Land on /financial:
    // it is the output of everything else in their nav, and it is the screen
    // they came to see.
    if (payloadOrUser?.organization_name === Organization.CROSS_RIVER_BANK) {
        return '/financial';
    }

    // Internal Blueberries. org — land on Nango Connections.
    if (payloadOrUser?.organization_name === Organization.BLUEBERRIES) {
        return '/integrations/connections';
    }

    // Otsar lands on Act 1 of its walkthrough (Connect), like Nova Capital above.
    // Data Analysis stays in its sidebar. Other connections-only orgs stay on Connections.
    if (isOtsarOrganization(payloadOrUser?.organization_name)) {
        return '/otsar/integrations';
    }

    // Corgi, Emporium, EKOLE, Galya, LYFE Service Inc., Rho, Shani Wigs — Connections only.
    if (isConnectionsOnlyOrg(payloadOrUser?.organization_name)) {
        return CONNECTIONS_HOME_PATH;
    }

    // Invest Capital client org: single-product experience — land straight on
    // Activity Reports. The dashboard payment gate still bounces unpaid members
    // to /welcome; paid members and impersonating admins go directly to the demo.
    if (
        payloadOrUser?.organization_name &&
        /invest[\s_-]?capital/i.test(payloadOrUser.organization_name)
    ) {
        return '/invest-capital';
    }

    // NEW (non-demo) organizations — self-provisioned by joining Plus or created
    // from Org Management going forward — get the SAME platform experience as
    // platform signups: the /welcome home (explore, validate plan, pay). Every
    // curated/real org (incl. Quinfica) is in ORGANIZATION_PAGE_ACCESS and keeps
    // its dedicated /workspace dashboard.
    if (
        payloadOrUser?.organization_name &&
        !curatedOrganizationKey(payloadOrUser.organization_name)
    ) {
        return '/welcome';
    }

    // Remaining curated demo orgs land on /workspace; the layout API
    // determines which page to show based on the active token.
    return '/workspace';

    // ── Legacy mappings (kept for reference during Phase 2-3 transition) ──
    // if (!payloadOrUser) return '/automated-collection';
    // if (payloadOrUser.role_code === 'ADMIN') return '/automated-collection';
    // else if (payloadOrUser.organization_name === 'Kavkom') return '/kpis-dashboard';
    // else if (payloadOrUser.organization_name === 'Ekstend') return '/billing-simulator';
    // else if (payloadOrUser.organization_name === 'Advanced Retail') return '/financial';
    // else if (payloadOrUser.organization_name === 'Restaura') return '/integrations';
    // else if (payloadOrUser.organization_name === 'Capital Lynk') return '/capital-lynk';
    // else if (payloadOrUser.organization_name === 'Orcom US') return '/journal-automation';
    // else if (payloadOrUser.organization_name === 'MD2') return '/md2pg';
    // return '/automated-collection';
}

